import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Drawer, Empty, InputNumber, message, Modal, Popconfirm, Select,
  Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  CheckCircleOutlined, DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined,
  FileProtectOutlined, PlayCircleOutlined, ReloadOutlined, SettingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { inspectionAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { useFitTableHeight } from '../hooks/useViewportFit';

interface ReportRow {
  id: number;
  period_key: string;
  trigger_type: 'auto' | 'manual';
  overall_status: 'OK' | 'WARN' | 'CRIT';
  ok_count: number;
  warn_count: number;
  crit_count: number;
  has_pdf: boolean;
  created_by: string;
  created_at: string;
}

interface InspectionItem {
  section: string;
  name: string;
  value: string;
  criteria: string;
  status: 'OK' | 'WARN' | 'CRIT' | 'INFO';
  detail: string;
}

const STATUS_META: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  OK: { color: '#10b981', icon: <CheckCircleOutlined />, label: '정상' },
  WARN: { color: '#f59e0b', icon: <WarningOutlined />, label: '주의' },
  CRIT: { color: '#ef4444', icon: <ExclamationCircleOutlined />, label: '심각' },
  INFO: { color: '#94a3b8', icon: <CheckCircleOutlined />, label: '참고' },
};

const SCHEDULE_LABEL: Record<string, string> = {
  monthly: '매월', weekly: '매주', daily: '매일', disabled: '사용 안 함',
};
const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일'];

const InspectionPage: React.FC = () => {
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('inspections', 'WRITE');
  const canDelete = hasPermission('inspections', 'DELETE');

  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  /* 표 본문 높이 실측 — 페이지 세로스크롤 없이 페이지네이션까지 뷰포트 안에 */
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [year, setYear] = useState<string>('');
  const [detail, setDetail] = useState<{ row: ReportRow; items: InspectionItem[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 주기 설정
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [schedType, setSchedType] = useState('monthly');
  const [anchorDay, setAnchorDay] = useState(1);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [schedSummary, setSchedSummary] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inspectionAPI.list(year || undefined);
      setReports(res.data.reports || []);
    } catch {
      message.error('점검일지 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await inspectionAPI.getSettings();
      setSchedType(res.data.schedule_type);
      setAnchorDay(res.data.anchor_day);
      setSchedSummary(formatSchedule(res.data.schedule_type, res.data.anchor_day));
    } catch { /* 설정 로드 실패는 치명적이지 않음 */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await inspectionAPI.run();
      const r = res.data.report;
      message.success(`점검 완료 — 종합 ${r.overall_status} (정상 ${r.ok_count} · 주의 ${r.warn_count} · 심각 ${r.crit_count})`);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '점검 실행에 실패했습니다');
    } finally {
      setRunning(false);
    }
  };

  const openDetail = async (row: ReportRow) => {
    setDetailLoading(true);
    setDetail({ row, items: [] });
    try {
      const res = await inspectionAPI.detail(row.id);
      setDetail({ row, items: res.data.data?.items || [] });
    } catch {
      message.error('상세 정보를 불러오지 못했습니다.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPdf = async (row: ReportRow) => {
    try {
      const res = await inspectionAPI.pdf(row.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sappt_inspection_${row.period_key}_${row.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('PDF 다운로드에 실패했습니다.');
    }
  };

  const remove = async (id: number) => {
    try {
      await inspectionAPI.remove(id);
      message.success('삭제되었습니다.');
      load();
    } catch {
      message.error('삭제에 실패했습니다.');
    }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await inspectionAPI.updateSettings({ schedule_type: schedType, anchor_day: anchorDay });
      setSchedSummary(formatSchedule(schedType, anchorDay));
      message.success('자동 점검 주기가 저장되었습니다.');
      setSettingsOpen(false);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '설정 저장 실패');
    } finally {
      setSettingsSaving(false);
    }
  };

  const yearOptions = useMemo(() => {
    const cur = new Date().getFullYear();
    return [cur, cur - 1, cur - 2].map(y => ({ value: String(y), label: `${y}년` }));
  }, []);

  const columns = [
    {
      title: '점검 기간', dataIndex: 'period_key', key: 'period_key', width: 120,
      render: (v: string, r: ReportRow) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: '실행', dataIndex: 'trigger_type', key: 'trigger_type', width: 90,
      render: (v: string) => v === 'auto'
        ? <Tag color="geekblue" style={{ borderRadius: 10 }}>정기(자동)</Tag>
        : <Tag style={{ borderRadius: 10 }}>수동</Tag>,
    },
    {
      title: '종합 등급', dataIndex: 'overall_status', key: 'overall_status', width: 110,
      render: (v: string) => {
        const m = STATUS_META[v] || STATUS_META.OK;
        return <Tag color={m.color} style={{ borderRadius: 10, fontWeight: 700 }}>{m.icon} {v}</Tag>;
      },
    },
    {
      title: '항목 판정', key: 'counts', width: 200,
      render: (_: any, r: ReportRow) => (
        <Space size={6}>
          <span style={{ color: '#10b981', fontWeight: 600, fontSize: 12 }}>정상 {r.ok_count}</span>
          <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 12 }}>주의 {r.warn_count}</span>
          <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 12 }}>심각 {r.crit_count}</span>
        </Space>
      ),
    },
    { title: '실행자', dataIndex: 'created_by', key: 'created_by', width: 90 },
    { title: '생성일시', dataIndex: 'created_at', key: 'created_at', width: 160 },
    {
      title: '', key: 'actions', width: 160,
      render: (_: any, r: ReportRow) => (
        <Space size={4}>
          <Button size="small" onClick={() => openDetail(r)}>상세</Button>
          <Tooltip title={r.has_pdf ? 'PDF 다운로드' : 'PDF 없음'}>
            <Button size="small" icon={<DownloadOutlined />} disabled={!r.has_pdf}
              onClick={() => downloadPdf(r)} />
          </Tooltip>
          {canDelete && (
            <Popconfirm title="이 점검보고서를 삭제할까요?" onConfirm={() => remove(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const detailSections = useMemo(() => {
    if (!detail) return [];
    const order: string[] = [];
    detail.items.forEach(i => { if (!order.includes(i.section)) order.push(i.section); });
    return order.map(sec => ({ section: sec, items: detail.items.filter(i => i.section === sec) }));
  }, [detail]);

  return (
    /* Content 자체가 20px 패딩을 가지므로 루트 패딩은 두지 않는다 — 하단 16px가 실측 밖에 남아 세로스크롤을 만든다 */
    <div>
      <Card
        style={{ borderRadius: 12, marginBottom: 12 }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              <FileProtectOutlined style={{ color: '#4f46e5', marginRight: 8 }} />
              시스템 점검일지
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              lab · partner · api 서비스 가용성, 호스트 자원, k3s/Docker, DB, 백업, OCI 비용을 정기 점검하고 PDF 보고서로 보존합니다.
              {schedSummary && <> &nbsp;|&nbsp; 자동 점검: <b>{schedSummary}</b> 06:30</>}
            </Typography.Text>
          </div>
          <Space>
            <Select
              allowClear placeholder="연도 전체" style={{ width: 110 }}
              options={yearOptions} value={year || undefined}
              onChange={v => setYear(v || '')}
            />
            <Button icon={<ReloadOutlined />} onClick={load} />
            {canWrite && (
              <>
                <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>주기 설정</Button>
                <Button type="primary" icon={<PlayCircleOutlined />} loading={running} onClick={runNow}>
                  지금 점검 실행
                </Button>
              </>
            )}
          </Space>
        </div>
      </Card>

      <div ref={cardWrapRef}>
      <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
        <div ref={tableWrapRef}>
        <Table<ReportRow>
          rowKey="id" columns={columns} dataSource={reports} loading={loading}
          size="middle"
          pagination={{
            defaultPageSize: 20, showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
          }}
          scroll={{ y: bodyHeight }}
          locale={{ emptyText: <Empty description="점검 기록이 없습니다. '지금 점검 실행'으로 첫 점검을 시작하세요." /> }}
        />
        </div>
      </Card>
      </div>

      {/* 상세 Drawer */}
      <Drawer
        open={!!detail} onClose={() => setDetail(null)} width={720}
        title={detail ? (
          <Space>
            <span>점검 상세 — {detail.row.period_key}</span>
            <Tag color={STATUS_META[detail.row.overall_status]?.color} style={{ borderRadius: 10, fontWeight: 700 }}>
              {detail.row.overall_status}
            </Tag>
          </Space>
        ) : ''}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
        ) : detailSections.map(({ section, items }) => (
          <div key={section} style={{ marginBottom: 20 }}>
            <Typography.Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>{section}</Typography.Text>
            <Table<InspectionItem>
              rowKey={(r) => `${r.section}-${r.name}`}
              dataSource={items} size="small" pagination={false}
              columns={[
                { title: '항목', dataIndex: 'name', width: 150 },
                {
                  title: '측정값', dataIndex: 'value',
                  render: (v: string, r: InspectionItem) => (
                    <>
                      <div style={{ fontSize: 12 }}>{v}</div>
                      {r.detail && <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.detail}</div>}
                    </>
                  ),
                },
                { title: '기준', dataIndex: 'criteria', width: 170, render: (v: string) => <span style={{ fontSize: 11, color: '#64748b' }}>{v}</span> },
                {
                  title: '판정', dataIndex: 'status', width: 70,
                  render: (v: string) => <Tag color={STATUS_META[v]?.color} style={{ borderRadius: 10, fontWeight: 600 }}>{v}</Tag>,
                },
              ]}
            />
          </div>
        ))}
        {detail && detail.row.has_pdf && (
          <Button block icon={<DownloadOutlined />} onClick={() => downloadPdf(detail.row)}>
            PDF 보고서 다운로드
          </Button>
        )}
      </Drawer>

      {/* 주기 설정 Modal */}
      <Modal
        open={settingsOpen} onCancel={() => setSettingsOpen(false)} onOk={saveSettings}
        confirmLoading={settingsSaving} title="자동 점검 주기 설정" okText="저장" cancelText="취소"
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          크론은 매일 06:30(KST)에 트리거되며, 아래 주기에 해당하는 날에만 점검이 실행됩니다.
          권장: <b>매월 1일</b> (실시간 감시는 Grafana·디스크 알람이 상시 수행 — 보고서형 점검은 월간이 적정 주기입니다).
        </Typography.Paragraph>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>주기</Typography.Text>
            <Select
              style={{ width: '100%' }} value={schedType}
              onChange={v => { setSchedType(v); setAnchorDay(v === 'weekly' ? 0 : 1); }}
              options={Object.entries(SCHEDULE_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </div>
          {schedType === 'monthly' && (
            <div>
              <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>실행일 (1~28일)</Typography.Text>
              <InputNumber min={1} max={28} value={anchorDay} onChange={v => setAnchorDay(v || 1)} style={{ width: '100%' }} />
            </div>
          )}
          {schedType === 'weekly' && (
            <div>
              <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>실행 요일</Typography.Text>
              <Select
                style={{ width: '100%' }} value={anchorDay} onChange={v => setAnchorDay(v)}
                options={WEEKDAYS.map((d, i) => ({ value: i, label: `${d}요일` }))}
              />
            </div>
          )}
        </Space>
      </Modal>
    </div>
  );
};

function formatSchedule(type: string, anchor: number): string {
  if (type === 'monthly') return `매월 ${anchor}일`;
  if (type === 'weekly') return `매주 ${WEEKDAYS[anchor % 7]}요일`;
  if (type === 'daily') return '매일';
  return '사용 안 함';
}

export default InspectionPage;
